/**
 * HitlChoicePanel — choice-based HITL response form, shared between ora and
 * the Outlook plugin.
 *
 * One canonical component for any HITL question whose payload is a
 * "review checkpoint" (legacy ``review_checkpoint`` type or any non-schema
 * question that surfaces choices/actions). Renders the question prompt,
 * an editable mortgagee list when the question seeds one, an optional notes
 * textarea, and pill buttons (one per choice).
 *
 * Why this exists: ora and the plugin previously each shipped their own
 * choice dispatchers. Plugin's ``ReviewCheckpointPanel`` wrapped the
 * response in ``{ approved: true, corrections: edits }`` and only included
 * the mortgagee draft when the user actually edited it — which produced
 * a different ``{approved, corrections}`` shape on the wire from ora's
 * ``{choice, mortgagee_draft: {mortgagees}}``. The HITL Decisions card
 * then rendered the two surfaces' decisions differently, even for the
 * same workflow. Mantra: USE THE SAME COMPONENT IN BOTH INTERFACES.
 *
 * The shared submitter:
 *   - always seeds the mortgagee list from
 *     ``hitlQuestion.mortgagee_draft.mortgagees`` (when present);
 *   - always submits ``{choice, notes, mortgagee_draft?}`` unwrapped — no
 *     legacy ``{approved, corrections}`` envelope;
 *   - includes the mortgagee draft on every submission whenever the
 *     question seeded one, regardless of edits.
 *   - requires a two-click confirmation for the last/destructive choice
 *     (when total choices > 1).
 *
 * Transports stay app-specific (ora uses Remix's ``useFetcher``, plugin
 * uses ``orchestratorApi.resumeInstance``) but the wire shape is now
 * identical.
 */

import React, { useEffect, useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import CustomTooltip from "~/common/components/CustomTooltip";
import { MortgageeList, type MortgageeRecord } from "./MortgageeList";
import {
  inferredOnly,
  weakestConfidence,
  type ConfidenceLevel,
} from "./confidenceChip";
import { getQuestionPrompt } from "../getQuestionPrompt";
import { renderInlineBold } from "../renderInlineBold";
import { parseObjectSchema } from "../schemaHelpers";
import JsonSchemaForm from "./JsonSchemaForm";
import { OptionRow } from "~/common/components/OptionRow";

/**
 * The substrate scores the extracted mortgagee per LAUNCH-FORM sub-field, keyed
 * to the dotted `"mortgagees.<subKey>"` address that `JsonSchemaForm` resolves.
 * This panel shows a flatter record — one `mailing_address` textarea where the
 * launch form has street / city / state / zip — so the four address components
 * roll up to their WEAKEST level.
 *
 * Conservative on purpose: the chip exists to tell the Servicer what deserves a
 * second look, and "one part of this address was a guess" is exactly that.
 * Taking the best, or averaging, would hide it.
 *
 * Unknown keys are ignored rather than dropped loudly — the substrate may score
 * fields this surface does not render, and that is not an error here.
 */
function mortgageeConfidence(
  fieldConfidence?: Record<string, ConfidenceLevel>,
): Partial<Record<keyof MortgageeRecord, ConfidenceLevel>> | undefined {
  if (!fieldConfidence) return undefined;
  const at = (...keys: string[]) =>
    weakestConfidence(keys.map((k) => fieldConfidence[k]));
  const mapped = {
    name: at("mortgagees.name", "mortgagees.mortgagee_name"),
    loan_number: at("mortgagees.loan_number"),
    mailing_address: at(
      "mortgagees.mailing_street",
      "mortgagees.mailing_city",
      "mortgagees.mailing_state",
      "mortgagees.mailing_zip",
      "mortgagees.mailing_address",
    ),
  };
  return Object.values(mapped).some((v) => v !== undefined) ? mapped : undefined;
}

export interface HitlChoicePanelProps {
  /** The HITL question payload, as forwarded from the substrate. */
  hitlQuestion: Record<string, unknown> | null | undefined;
  /**
   * Called with the unwrapped response payload. Always shaped as
   * ``{choice, notes, mortgagee_draft?}`` — never the legacy ``{approved,
   * corrections}`` envelope. The parent wires this to its transport.
   */
  onSubmit: (payload: Record<string, unknown>) => void | Promise<void>;
  /** External submitting state — disables every input + shows spinner. */
  isSubmitting?: boolean;
  /** Optional pre-prompt rendered above the question text (e.g. status pill). */
  prefix?: React.ReactNode;
  /**
   * Per-field extraction confidence, keyed by the substrate's dotted address.
   * Forwarded from `HitlQuestionPanel`; mapped onto the mortgagee controls by
   * `mortgageeConfidence` above. Only email-INFERRED fields are scored —
   * policy, term and client values are Level 2 lookups and carry no chip.
   */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  /** Optional content rendered between mortgagees and the action buttons (e.g. screenshot grid). */
  children?: React.ReactNode;
  /**
   * @deprecated Override the default approve button label.
   * No longer used in JSX — pill buttons use choice strings directly.
   * Kept for backward compatibility with the Outlook plugin.
   */
  approveLabel?: string;
  /**
   * @deprecated Override the default reject button label.
   * No longer used in JSX — pill buttons use choice strings directly.
   * Kept for backward compatibility with the Outlook plugin.
   */
  rejectLabel?: string;
}

/**
 * Extract choices from the hitlQuestion payload.
 * Falls back to ["Approve", "Reject"] when no choices/actions are found.
 */
export function getChoices(
  hitlQuestion: Record<string, unknown> | null | undefined,
): string[] {
  if (hitlQuestion && typeof hitlQuestion === "object") {
    const choices = (hitlQuestion as Record<string, unknown>).choices;
    if (Array.isArray(choices) && choices.every((c) => typeof c === "string")) {
      return choices as string[];
    }
    const actions = (hitlQuestion as Record<string, unknown>).actions;
    if (Array.isArray(actions) && actions.every((c) => typeof c === "string")) {
      return actions as string[];
    }
  }
  return ["Approve", "Reject"];
}

function isMortgageeArray(v: unknown): v is MortgageeRecord[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((m) => typeof m === "object" && m !== null)
  );
}

/** Per-row metadata the substrate ships when the policy search returned
 * >1 matching row (renewed-policy case). Mirrors the dict shape
 * ``_build_policy_term_choices`` in
 * ``workflows/rpa/epic_realtime.py`` writes. */
export interface PolicyTermChoice {
  match_index: number;
  automation_id: string;
  eff_from: string;
  eff_to: string;
  label: string;
  picked: boolean;
}

function getPolicyTermChoices(
  hitlQuestion: Record<string, unknown> | null | undefined,
): PolicyTermChoice[] {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return [];
  const raw = (hitlQuestion as Record<string, unknown>).policy_term_choices;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c): c is PolicyTermChoice =>
      typeof c === "object" &&
      c !== null &&
      typeof (c as PolicyTermChoice).match_index === "number" &&
      typeof (c as PolicyTermChoice).label === "string",
  );
}

function getPickedTermMatchIndex(
  hitlQuestion: Record<string, unknown> | null | undefined,
  choices: PolicyTermChoice[],
): number | null {
  if (hitlQuestion && typeof hitlQuestion === "object") {
    const raw = (hitlQuestion as Record<string, unknown>)
      .picked_policy_row_match_index;
    if (typeof raw === "number") return raw;
  }
  const picked = choices.find((c) => c.picked);
  return picked ? picked.match_index : choices[0]?.match_index ?? null;
}

/**
 * A selectable Additional-Interest row (or the synthetic "add new" option)
 * surfaced by the substrate when the AM must pick which interest to update.
 * Rendered as a radio group — the SAME UX as the policy-term picker — so the AM
 * chooses by the mortgagee name shown, never by a row number.
 */
export interface AdditionalInterestChoice {
  /** 0-based row index for the RPA edit; the "add new" option uses a negative sentinel. */
  index: number;
  label: string;
  /** True for the synthetic "Add a new interest instead" option. */
  add_new?: boolean;
}

function getAdditionalInterestChoices(
  hitlQuestion: Record<string, unknown> | null | undefined,
): AdditionalInterestChoice[] {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return [];
  const raw = (hitlQuestion as Record<string, unknown>).additional_interest_choices;
  if (!Array.isArray(raw)) return [];
  const out: AdditionalInterestChoice[] = [];
  for (const c of raw) {
    if (
      c &&
      typeof c === "object" &&
      typeof (c as Record<string, unknown>).index === "number" &&
      typeof (c as Record<string, unknown>).label === "string"
    ) {
      const rec = c as Record<string, unknown>;
      out.push({
        index: rec.index as number,
        label: rec.label as string,
        add_new: rec.add_new === true,
      });
    }
  }
  return out;
}

/** Update-flavored default kept for in-flight instances whose question predates
 * ``additional_interest_prompt`` — on those the pick was always an update. */
const DEFAULT_ADDITIONAL_INTEREST_PROMPT =
  "Pick the interest to update with the new mortgagee — or add a new one:";

/** Radio-group prompt above the interest choices. Server-owned copy: the
 * substrate ships an action-aware sentence (e.g. "Pick the interest to
 * remove:" on a delete) so a removal is never captioned as an update. */
function getAdditionalInterestPrompt(
  hitlQuestion: Record<string, unknown> | null | undefined,
): string {
  if (!hitlQuestion || typeof hitlQuestion !== "object") {
    return DEFAULT_ADDITIONAL_INTEREST_PROMPT;
  }
  const raw = (hitlQuestion as Record<string, unknown>).additional_interest_prompt;
  return typeof raw === "string" && raw.trim()
    ? raw
    : DEFAULT_ADDITIONAL_INTEREST_PROMPT;
}

function getInitialMortgagees(
  hitlQuestion: Record<string, unknown> | null | undefined,
): MortgageeRecord[] {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return [];
  const draft = (hitlQuestion as Record<string, unknown>).mortgagee_draft;
  if (!draft || typeof draft !== "object") return [];
  const m = (draft as Record<string, unknown>).mortgagees;
  return isMortgageeArray(m) ? m : [];
}

/**
 * Schema properties HitlChoicePanel already handles through its own chrome:
 * ``decision`` is the pill row, ``notes``/``note`` is the reject-reason
 * textarea. Every OTHER schema property is an "extra input" the gate needs
 * filled — e.g. the MFA method radio (``method``) and the 6-digit code
 * (``code``). Those must be rendered and their values submitted, or the AM
 * is shown only Approve/Reject and the run resumes with an empty value
 * (regression: instance 1e942fe2, ``run_mfa_code_submit: invalid code ''``).
 */
const RESERVED_SCHEMA_KEYS = new Set(["decision", "notes", "note"]);

interface ExtraSchemaInputs {
  /** Parsed extra fields (excludes decision/notes) — empty for plain gates. */
  keys: string[];
  /** Filtered ``type:object`` schema fed to a controlled JsonSchemaForm. */
  schema: Record<string, unknown> | null;
  /** Seed values from each property's ``default`` (e.g. method → text_sms). */
  defaults: Record<string, unknown>;
  /** Required extra keys (intersection of schema.required and extra keys). */
  required: string[];
  /** Per-key ``pattern`` (e.g. code → ^[0-9]{6}$) for client-side validation. */
  patterns: Record<string, string>;
}

const EMPTY_EXTRA_INPUTS: ExtraSchemaInputs = {
  keys: [],
  schema: null,
  defaults: {},
  required: [],
  patterns: {},
};

/**
 * Extract the schema-declared input fields a review_checkpoint gate needs the
 * AM to fill, beyond the decision pills / reject notes HitlChoicePanel already
 * renders. Returns empty for the common decision+notes-only schema, so plain
 * gates (policy-term, mortgagee) are completely unchanged.
 */
function getExtraSchemaInputs(
  hitlQuestion: Record<string, unknown> | null | undefined,
): ExtraSchemaInputs {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return EMPTY_EXTRA_INPUTS;
  const rawSchema = (hitlQuestion as Record<string, unknown>).schema;
  if (!rawSchema || typeof rawSchema !== "object") return EMPTY_EXTRA_INPUTS;
  const parsed = parseObjectSchema(rawSchema as Record<string, unknown>);
  if (!parsed) return EMPTY_EXTRA_INPUTS;
  const extra = parsed.filter((f) => !RESERVED_SCHEMA_KEYS.has(f.key));
  if (extra.length === 0) return EMPTY_EXTRA_INPUTS;

  const keep = new Set(extra.map((f) => f.key));
  const props =
    ((rawSchema as Record<string, unknown>).properties as
      | Record<string, Record<string, unknown>>
      | undefined) ?? {};
  const filteredProps: Record<string, unknown> = {};
  const defaults: Record<string, unknown> = {};
  const patterns: Record<string, string> = {};
  for (const f of extra) {
    const p = props[f.key];
    if (!p || typeof p !== "object") continue;
    filteredProps[f.key] = p;
    if (p.default !== undefined) defaults[f.key] = p.default;
    if (typeof p.pattern === "string") patterns[f.key] = p.pattern;
  }
  const requiredRaw = Array.isArray((rawSchema as Record<string, unknown>).required)
    ? ((rawSchema as Record<string, unknown>).required as string[])
    : [];
  return {
    keys: extra.map((f) => f.key),
    schema: { type: "object", properties: filteredProps, required: requiredRaw.filter((k) => keep.has(k)) },
    defaults,
    required: requiredRaw.filter((k) => keep.has(k)),
    patterns,
  };
}

/** Regex test that treats an un-compilable pattern as "no constraint". */
function matchesPattern(pattern: string, value: string): boolean {
  try {
    return new RegExp(pattern).test(value);
  } catch {
    return true;
  }
}

/** Return the tooltip text for a given choice, or null if absent/empty. */
function getTooltip(
  hitlQuestion: Record<string, unknown> | null | undefined,
  choice: string,
): string | null {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return null;
  const tooltips = (hitlQuestion as Record<string, unknown>).tooltips;
  if (!tooltips || typeof tooltips !== "object" || Array.isArray(tooltips)) return null;
  const tip = (tooltips as Record<string, unknown>)[choice];
  if (typeof tip === "string" && tip.trim().length > 0) return tip.trim();
  return null;
}

/**
 * Return the display label for a choice value, or null to fall back to the
 * raw choice string. Keyed on the same decision VALUE as `tooltips` (e.g.
 * "approve"/"reject") — the label only changes what's rendered on the pill;
 * `submit()` always sends the raw `choice`, so the decision contract (schema
 * enum, x-destructive marker, resume validation) is untouched. See
 * _carrier_action_copy.py (workflows repo) for the substrate side.
 */
export function getChoiceLabel(
  hitlQuestion: Record<string, unknown> | null | undefined,
  choice: string,
): string | null {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return null;
  const labels = (hitlQuestion as Record<string, unknown>).choice_labels;
  if (!labels || typeof labels !== "object" || Array.isArray(labels)) return null;
  const label = (labels as Record<string, unknown>)[choice];
  if (typeof label === "string" && label.trim().length > 0) return label.trim();
  return null;
}

/** Pill button styling. All pills get a full colored border that's visible
 *  against the fill:
 *  - Destructive choice → white bg + red border (btn-error-secondary)
 *  - First (non-destructive) → sunrise filled + darker blossom border
 *  - Anything else → white bg + gray border (btn-secondary-gray)
 */
function getPillClasses(
  choice: string,
  index: number,
  destructiveChoice: string | null,
): string {
  const base = "flex-1 text-sm rounded-lg border-[1px] border-solid px-[0.75rem] py-[0.5rem] font-bold transition-shadow";
  if (choice === destructiveChoice) return `${base} btn-error-secondary`;
  if (index === 0) return `${base} btn-primary !border-blossom`;
  return `${base} btn-secondary-gray`;
}

export function HitlChoicePanel({
  hitlQuestion,
  onSubmit,
  isSubmitting = false,
  prefix,
  children,
  fieldConfidence,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  approveLabel: _approveLabel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rejectLabel: _rejectLabel,
}: HitlChoicePanelProps) {
  const question = getQuestionPrompt(hitlQuestion);
  const choices = getChoices(hitlQuestion);
  const termChoices = getPolicyTermChoices(hitlQuestion);
  const [mortgageeList, setMortgageeList] = useState<MortgageeRecord[]>(() =>
    getInitialMortgagees(hitlQuestion),
  );
  const [notes, setNotes] = useState("");
  const [pendingDestructive, setPendingDestructive] = useState<string | null>(null);
  const [selectedTermMatchIndex, setSelectedTermMatchIndex] = useState<
    number | null
  >(() => getPickedTermMatchIndex(hitlQuestion, termChoices));

  // Re-sync the preselected term when the substrate advances to a DIFFERENT
  // gate. This panel is NOT remounted between consecutive HITL gates —
  // WorkflowDetailDrawer reuses one instance across the run — so the useState
  // initializer (which runs once) captured the value for the FIRST gate
  // rendered. When that gate has no policy_term_choices (e.g.
  // verify_epic_customer) the initial value is null, and it stayed null when
  // verify_policy_detail arrived with the two-term picker → NEITHER radio
  // preselected. Keying the effect on the picker's stable identity (the
  // choices' match_index set + the picker's auto-choice) fires it on a genuine
  // gate change but NOT on status-poll re-renders of the SAME question (those
  // only re-sign SAS URLs; match_index / picked_policy_row_match_index are
  // stable), so a user's manual override within a gate is preserved.
  const termPickerIdentity = `${termChoices
    .map((c) => c.match_index)
    .join(",")}|${getPickedTermMatchIndex(hitlQuestion, termChoices) ?? "none"}`;
  useEffect(() => {
    setSelectedTermMatchIndex(getPickedTermMatchIndex(hitlQuestion, termChoices));
    // termPickerIdentity is the stable identity of the two inputs below; keying
    // on it avoids clobbering a manual selection on same-gate re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termPickerIdentity]);

  // Schema-declared extra inputs (MFA method radio, 6-digit code). Empty for
  // the common decision+notes gates, so nothing changes there.
  const extraInputs = useMemo(() => getExtraSchemaInputs(hitlQuestion), [hitlQuestion]);
  const [extraValues, setExtraValues] = useState<Record<string, unknown>>(
    () => extraInputs.defaults,
  );
  const [extraError, setExtraError] = useState(false);
  // Reseed when the substrate advances to a DIFFERENT gate (schema identity
  // changes) — but NOT on same-gate SAS re-polls (schema is stable across
  // those), so a half-typed code survives a status refresh. Mirrors the
  // policy-term picker's identity-keyed resync above.
  const extraIdentity = useMemo(
    () => JSON.stringify(extraInputs.schema),
    [extraInputs.schema],
  );
  useEffect(() => {
    setExtraValues(extraInputs.defaults);
    setExtraError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraIdentity]);

  // Approve is blocked until every required extra input is present AND matches
  // its pattern. This is the guard that stops an empty-code resume — the exact
  // failure that broke instance 1e942fe2.
  const extraInputsSatisfied = useMemo(() => {
    for (const key of extraInputs.keys) {
      const v = extraValues[key];
      const isEmpty = v === undefined || v === null || v === "";
      if (extraInputs.required.includes(key) && isEmpty) return false;
      const pattern = extraInputs.patterns[key];
      if (!isEmpty && pattern && !matchesPattern(pattern, String(v))) return false;
    }
    return true;
  }, [extraInputs, extraValues]);

  // Additional-Interest row picker (reuses the policy-term radio UX): the AM
  // picks the interest to update — by mortgagee NAME, not a row number — or the
  // "add a new interest instead" option. Empty for every other gate.
  const aiChoices = useMemo(
    () => getAdditionalInterestChoices(hitlQuestion),
    [hitlQuestion],
  );
  const [selectedAiIndex, setSelectedAiIndex] = useState<number | null>(null);
  const [aiError, setAiError] = useState(false);
  // Reset the selection on a genuine gate change (the choice-index set changes),
  // NOT on same-gate SAS re-polls — mirrors the policy-term resync. No default
  // pre-selection: the AM must consciously choose which interest to touch.
  const aiChoicesIdentity = useMemo(
    () => aiChoices.map((c) => c.index).join(","),
    [aiChoices],
  );
  useEffect(() => {
    setSelectedAiIndex(null);
    setAiError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiChoicesIdentity]);
  const aiChoiceSatisfied = aiChoices.length === 0 || selectedAiIndex !== null;

  const submit = (choice: string) => {
    // Stamp ``decision`` alongside the legacy ``choice`` on EVERY submission,
    // not just when extra schema inputs are present. A plain decision+notes
    // schema (e.g. a `failure_recovery` gate's retry/skip/abort enum) has no
    // extra keys, so the old `if (extraInputs.keys.length > 0)` guard left
    // `decision` off the wire entirely for it. `canonical_hitl_action` reads
    // `choice` as its last-resort fallback but only recognizes approve/reject
    // tokens; `_parse_hitl_decision` (the retry/skip/abort reader) read only
    // `action`/`decision`/`field_values.decision` and defaulted anything else
    // to "abort" — so a Retry click on that gate was silently recorded as an
    // abort server-side (instance 58ad38da-678f-4859-bd45-250e1efbfffa, dev,
    // 2026-09-17). Redundant with `choice` for every other gate; harmless.
    const payload: Record<string, unknown> = { choice, decision: choice, notes };
    // Always include the mortgagee state when the question seeded one —
    // matches ora's prior behavior and ensures the AM's view of the
    // mortgagees is captured even when no edits were made.
    if (mortgageeList.length > 0) {
      payload.mortgagee_draft = { mortgagees: mortgageeList };
    }
    // Surface the AM's policy-term choice when the question presented one.
    // The workflow's _apply_policy_term_override_if_any re-stamps state
    // when this differs from the picker's auto-choice — see
    // workflows/graphs/orchestrations/carrier_mortgagee_update.py.
    if (termChoices.length > 1 && selectedTermMatchIndex !== null) {
      payload.policy_term_match_index = selectedTermMatchIndex;
    }
    // Schema-declared inputs (MFA method / 6-digit code) ride under
    // ``field_values`` — the channel the substrate reads them from (see
    // _epic_mfa_click_method / _epic_mfa_submit_code, which do
    // ``hitl_answer["field_values"].get("method"|"code")``).
    if (extraInputs.keys.length > 0) {
      payload.field_values = { ...extraValues };
    }
    // Additional-Interest row pick → submit under the keys the substrate
    // already reads (``_picked_row_index`` reads ``row_index``; ``_picker_add_new``
    // reads ``add_new``), so no substrate reader change is needed. The "add new"
    // option carries add_new; a real row carries its 0-based row_index.
    if (aiChoices.length > 0 && selectedAiIndex !== null) {
      const chosen = aiChoices.find((c) => c.index === selectedAiIndex);
      if (chosen?.add_new) {
        payload.add_new = true;
      } else {
        payload.row_index = selectedAiIndex;
      }
    }
    void onSubmit(payload);
  };

  // Schema-driven destructive marker: when the substrate flags a specific
  // choice as destructive via `x-destructive`, clicking it shows an inline
  // confirmation. No position-based default — explicit marker required.
  const destructiveChoice = ((): string | null => {
    if (!hitlQuestion || typeof hitlQuestion !== "object") return null;
    const x = (hitlQuestion as Record<string, unknown>)["x-destructive"];
    return typeof x === "string" && x.length > 0 ? x : null;
  })();

  const handleChoiceClick = (choice: string) => {
    if (choice === destructiveChoice) {
      // Reject/abort never needs the extra inputs (you're cancelling) — go
      // straight to the confirmation strip.
      setPendingDestructive(choice);
      return;
    }
    // Non-destructive (approve): block until required schema inputs are valid.
    if (extraInputs.keys.length > 0 && !extraInputsSatisfied) {
      setExtraError(true);
      return;
    }
    // Block until the AM has picked an Additional-Interest row (or "add new") —
    // never resume with an implicit row-0 edit.
    if (aiChoices.length > 0 && !aiChoiceSatisfied) {
      setAiError(true);
      return;
    }
    submit(choice);
  };

  // Every rejection requires a non-empty comment — guards both the submit
  // path and the button-disabled state so the AM can never abort without
  // recording why. Mirrors the substrate-side conditional schema in
  // `_build_review_checkpoint_question`, which fires the requirement only
  // when "reject" is in the decision set. Other destructive choices (e.g.
  // a three-way picker's "gamma" branch) keep the optional-notes UX.
  const rejectReasonRequired =
    pendingDestructive !== null &&
    pendingDestructive.toLowerCase() === "reject";
  const rejectReasonMissing = rejectReasonRequired && notes.trim().length === 0;

  const confirmDestructive = () => {
    if (pendingDestructive === null) return;
    if (rejectReasonMissing) return;
    submit(pendingDestructive);
  };

  const cancelDestructive = () => {
    setPendingDestructive(null);
    // Notes was specifically for the rejection — discard if the user backs out.
    setNotes("");
  };

  // Display-only label for the confirm strip — submit(pendingDestructive)
  // above still sends the raw value, this only affects what the AM reads.
  const pendingDestructiveLabel =
    pendingDestructive !== null
      ? getChoiceLabel(hitlQuestion, pendingDestructive) ?? pendingDestructive
      : null;

  // isOnModal → CustomTooltip's Popper renders at zIndex 1400, above the
  // Outlook add-in's bottom Drawer (MUI modal, zIndex 1300) — without it the
  // tooltip is logically wired and populated but paints invisibly BEHIND the
  // drawer. Mirrors the step tooltip in InstanceDetailDrawer, which already
  // passes isOnModal for the same reason.
  // The <span> wrapper is required for the tooltip to fire while the button
  // is `disabled` (mid-submit) — MUI Tooltip does not attach pointer/focus
  // listeners to a disabled element, only to an ancestor. `flex flex-1` keeps
  // the span a transparent layout passthrough so the button's own `flex-1`
  // pill sizing (getPillClasses) is unaffected.
  const withTooltip = (tip: string | null, id: string, el: React.ReactElement) =>
    tip ? (
      <CustomTooltip id={id} title={tip} arrow isOnModal>
        <span className="flex flex-1">{el}</span>
      </CustomTooltip>
    ) : (
      el
    );

  return (
    <div className="flex flex-col gap-3" data-testid="hitl-choice-panel">
      {prefix}
      {/* whitespace-pre-wrap preserves \n\n paragraph breaks the substrate
          includes in pending_action — without it the text collapses into a
          single line and instructions stop being scannable. */}
      <p className="text-sm text-primary-gray-light-800 whitespace-pre-wrap">
        {renderInlineBold(question)}
      </p>

      {/* Policy-term picker — only renders when the policy search
          returned >1 matching row. AM defaults to the substrate's
          auto-choice (max start date) and can override before
          approving. See workflows/rpa/epic_realtime.py — picker emits
          ``policy_term_choices`` only when row_count > 1. */}
      {termChoices.length > 1 && (
        <fieldset
          className="flex flex-col gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
          data-testid="hitl-policy-term-picker"
        >
          <legend className="text-[11px] uppercase tracking-wide text-primary-gray-light-500 font-semibold px-1">
            Policy term
          </legend>
          <p className="text-xs text-primary-gray-light-600">
            Epic returned {termChoices.length} matching terms. Confirm the
            one you want to operate on:
          </p>
          {termChoices.map((c) => (
            <OptionRow
              key={c.match_index}
              name="policy_term_match_index"
              value={String(c.match_index)}
              checked={selectedTermMatchIndex === c.match_index}
              onChange={() => setSelectedTermMatchIndex(c.match_index)}
              disabled={isSubmitting}
              testId={`hitl-policy-term-choice-${c.match_index}`}
              className="text-sm text-primary-gray-light-900"
            >
              {c.label}
              {c.picked && (
                <span className="ml-2 text-2xs font-semibold uppercase text-primary-gray-light-500">
                  (picked)
                </span>
              )}
            </OptionRow>
          ))}
        </fieldset>
      )}

      {mortgageeList.length > 0 && (
        <MortgageeList
          mortgagees={mortgageeList}
          onChange={setMortgageeList}
          disabled={isSubmitting}
          fieldConfidence={mortgageeConfidence(fieldConfidence)}
        />
      )}

      {children}

      {/* Additional-Interest row picker — radio list of the existing interests
          (labelled by mortgagee NAME, not a row number) plus an "add a new
          interest" option. Same radio UX as the policy-term picker above; the
          grid screenshot rides in ``children`` (steps_reviewed) so the AM sees
          what they're choosing from. Absent for every other gate. */}
      {aiChoices.length > 0 && (
        <fieldset
          className="flex flex-col gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 px-3 py-2"
          data-testid="hitl-ai-picker"
        >
          <legend className="text-[11px] uppercase tracking-wide text-primary-gray-light-500 font-semibold px-1">
            Additional Interest
          </legend>
          <p className="text-xs text-primary-gray-light-600">
            {getAdditionalInterestPrompt(hitlQuestion)}
          </p>
          {aiChoices.map((c) => (
            <OptionRow
              key={c.index}
              name="additional_interest_choice"
              value={String(c.index)}
              checked={selectedAiIndex === c.index}
              onChange={() => {
                setSelectedAiIndex(c.index);
                setAiError(false);
              }}
              disabled={isSubmitting}
              testId={`hitl-ai-choice-${c.index}`}
              className="text-sm text-primary-gray-light-900"
            >
              {c.label}
            </OptionRow>
          ))}
          {aiError && (
            <p
              className="text-[11px] text-primary-error-600"
              data-testid="hitl-ai-picker-error"
            >
              Select an option above before continuing.
            </p>
          )}
        </fieldset>
      )}

      {/* Schema-declared inputs the gate needs before Approve — e.g. the MFA
          method radio and the 6-digit verification code. Rendered via the
          shared JsonSchemaForm (controlled, no footer: the Decision pills below
          are the only commit affordance). Absent for plain decision+notes
          gates. */}
      {extraInputs.keys.length > 0 && extraInputs.schema && (
        <div className="flex flex-col gap-1" data-testid="hitl-schema-inputs">
          <JsonSchemaForm
            schema={extraInputs.schema}
            value={extraValues}
            onChange={(next) => {
              setExtraValues(next);
              setExtraError(false);
            }}
            submitting={isSubmitting}
            // Story 27442: the launch form has always chipped these fields and
            // the decision surface never did, so the Servicer lost the one
            // signal telling them what deserves a second look at exactly the
            // moment they commit. `inferredOnly` is the gate: a gate that asks
            // for a policy number, a term date or an MFA code gets NO chip,
            // because nothing inferred those and a chip would claim otherwise.
            fieldConfidence={inferredOnly(fieldConfidence)}
          />
          {extraError && (
            <p
              className="text-[11px] text-primary-error-600"
              data-testid="hitl-schema-inputs-error"
            >
              Enter the requested value above before continuing.
            </p>
          )}
        </div>
      )}

      {/* Pill buttons */}
      <div className="flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-wide text-primary-gray-light-500 font-semibold">
          Decision <span className="text-primary-error-400">*</span>
        </p>
        <div className="flex gap-3">
          {choices.map((choice, index) => {
            const tip = getTooltip(hitlQuestion, choice);
            const label = getChoiceLabel(hitlQuestion, choice) ?? choice;
            const testId = `hitl-choice-${choice.toLowerCase().replace(/\s+/g, "-")}`;
            const btn = (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleChoiceClick(choice)}
                className={getPillClasses(choice, index, destructiveChoice)}
                data-testid={testId}
                data-tooltip-title={tip ?? undefined}
              >
                {label}
              </button>
            );
            return (
              <React.Fragment key={choice}>
                {withTooltip(tip, testId, btn)}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Inline confirmation strip for destructive choice — now hosts the
          Notes textarea so users can explain what was wrong before submit. */}
      <Collapse in={pendingDestructive !== null} unmountOnExit timeout={250}>
        <div className="border border-primary-error-300 bg-primary-error-50 rounded-md px-3 py-2 flex flex-col gap-3">
          <p className="text-xs text-primary-error-700">
            Are you sure you want to{" "}
            <strong className="text-primary-error-900">{pendingDestructiveLabel}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex flex-col gap-1">
            <label
              className="text-[11px] uppercase tracking-wide text-primary-error-700 font-semibold"
              htmlFor="hitl-reject-reason"
            >
              {rejectReasonRequired ? (
                <>
                  Reason <span className="text-primary-error-400">*</span>{" "}
                  <span className="normal-case text-primary-error-500">
                    (required — what was wrong?)
                  </span>
                </>
              ) : (
                <>
                  Notes{" "}
                  <span className="normal-case text-primary-error-500">
                    (optional — what was wrong?)
                  </span>
                </>
              )}
            </label>
            <textarea
              id="hitl-reject-reason"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSubmitting}
              required={rejectReasonRequired}
              aria-required={rejectReasonRequired}
              aria-invalid={rejectReasonMissing}
              placeholder={
                rejectReasonRequired
                  ? "A reason is required to reject…"
                  : "Provide a reason, instructions, or an MFA code…"
              }
              autoFocus
              data-testid="hitl-reject-reason"
              className="rounded-md border border-primary-error-300 bg-base-white px-3 py-2 text-sm focus:outline-none focus:border-primary-error-500 focus:ring-1 focus:ring-primary-error-500 disabled:opacity-50 resize-none"
            />
            {rejectReasonMissing && (
              <p
                className="text-[11px] text-primary-error-600"
                data-testid="hitl-reject-reason-error"
              >
                Please enter a reason before rejecting.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isSubmitting || rejectReasonMissing}
              onClick={confirmDestructive}
              className="btn-error-primary text-xs font-semibold px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="hitl-confirm-destructive"
            >
              {isSubmitting ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                `Yes, ${pendingDestructiveLabel}`
              )}
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={cancelDestructive}
              className="btn-secondary-gray text-xs font-semibold !px-3 !py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      </Collapse>
    </div>
  );
}

export default HitlChoicePanel;
